import React from 'react';
import { TooltipProvider } from '@onward-delivery/design';
import * as ROUTES from '@/constants/routes';
import { useDrillIn } from '@/components/DrillInPage';

import BucketTabs from './BucketTabs';
import OnboardingFilters from './OnboardingFilters';
import OnboardingRow from './OnboardingRow';
import PanelMessage from './PanelMessage';
import SelectionPanel from './SelectionPanel';
import { useCarrierOnboarding } from '../hooks';
import { groupBySubBucket, selectionActions } from '../utils';

/**
 * The Carriers onboarding view: the four tabs, the active tab's cards under its section headers, and the options panel
 * for checked cards. A card's name opens the carrier's page.
 *
 * @param {{ search: string }} props
 */
const CarrierOnboarding = ({ search }) => {
    // The carrier page's back button returns to this view, on the same kind, view and tab.
    const drillIn = useDrillIn();
    const openCarrier = (row) => drillIn(ROUTES.corporateCarrierPath(row.carrier_id));
    const {
        loading,
        error,
        hasOrganization,
        bucket,
        setBucketKey,
        filters,
        setFilters,
        counts,
        bucketRows,
        selectedIds,
        selectedRows,
        toggleRow,
        clearSelection,
        busy,
        reinvite,
        setIntegrationsRequired,
    } = useCarrierOnboarding({ search });

    const { invitable, markNoIntegrations, requireIntegrations } = selectionActions(selectedRows, bucket.key);
    const panelButtons = [
        {
            key: 'INVITE',
            label: 'Invite',
            variant: 'outline',
            show: invitable.length > 0,
            onClick: () => reinvite(invitable, selectedRows.length - invitable.length),
        },
        {
            key: 'NO_INTEGRATIONS',
            label: 'Mark No Integrations Required',
            variant: 'outline',
            show: markNoIntegrations.length > 0,
            onClick: () => setIntegrationsRequired(markNoIntegrations, false),
        },
        {
            // Only flagged carriers get this; Mark No Integrations Required takes only unflagged ones, so a card never gets both.
            key: 'REQUIRE_INTEGRATIONS',
            label: 'Require Integrations',
            variant: 'outline',
            show: requireIntegrations.length > 0,
            sizes: false,
            onClick: () => setIntegrationsRequired(requireIntegrations, true),
        },
    ].map((button) => ({ ...button, disabled: busy }));

    let body;
    if (!hasOrganization) body = <PanelMessage>Your account is not associated with an organization.</PanelMessage>;
    else if (error) body = <PanelMessage>Couldn&apos;t load onboarding. Try refreshing the page.</PanelMessage>;
    else if (loading) body = <PanelMessage>Loading…</PanelMessage>;
    else if (!bucketRows.length) {
        const filtered = filters.types.length || filters.badges.length;
        body = <PanelMessage>{filtered ? 'No carriers here match the filters.' : 'No carriers here.'}</PanelMessage>;
    } else {
        body = (
            <div className="flex flex-col gap-5">
                {groupBySubBucket(bucketRows, bucket).map((section) => (
                    <section key={section.key} aria-label={section.label}>
                        <h3 className="mb-2.5 text-[15px] font-bold text-slate-900">
                            {section.label} - {section.rows.length}
                        </h3>
                        <ul className="flex flex-col gap-2.5">
                            {section.rows.map((row) => (
                                <OnboardingRow
                                    key={row.carrier_id}
                                    row={row}
                                    checked={selectedIds.has(row.carrier_id)}
                                    onToggle={toggleRow}
                                    onOpen={openCarrier}
                                />
                            ))}
                        </ul>
                    </section>
                ))}
            </div>
        );
    }

    // Radix tooltips throw without a provider above them: the options panel's button reasons. Radix
    // waits 700ms by default before opening; a shorter delay makes a hover over a button feel responsive.
    return (
        <TooltipProvider delayDuration={200}>
            <div className="flex min-h-0 w-full flex-1 flex-col">
                <BucketTabs activeKey={bucket.key} counts={counts} onSelect={setBucketKey} />
                <section
                    className={`flex min-h-0 flex-1 flex-col rounded-b-lg rounded-tr-lg border ${bucket.panelClass}`}
                >
                    <div className="min-h-0 flex-1 overflow-y-auto p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <p className="text-[13px] text-slate-600">{bucket.subtitle}</p>
                            <OnboardingFilters filters={filters} onChange={setFilters} />
                        </div>
                        {body}
                        {selectedRows.length ? (
                            <SelectionPanel
                                count={selectedRows.length}
                                buttons={panelButtons}
                                onClear={clearSelection}
                            />
                        ) : null}
                    </div>
                </section>
            </div>
        </TooltipProvider>
    );
};

export default CarrierOnboarding;
